/**
 * Small on/off for the Vest NQ-PERP candle overlay (MNQ/NQ price pane), in
 * the style of the volume pane's eye toggle: faint until hovered, 32px hit
 * area. While on it also shows the current Vest - CME gap being subtracted
 * ("Vest −25.25"). State: vestCandleOverlayStore (persisted, off by default).
 */
import React from 'react';
import { CandlestickChart } from 'lucide-react';
import { useVestCandleOverlayStore } from '../../store/vestCandleOverlayStore';
import { gapLabel, GAP_WINDOW } from '../../utils/vestCandles';

export interface VestCandleToggleProps {
  /** Gap applied to the newest drawn Vest candle; null = none drawn yet. */
  gap: number | null;
}

export const VestCandleToggle: React.FC<VestCandleToggleProps> = ({ gap }) => {
  const enabled = useVestCandleOverlayStore((s) => s.enabled);
  const toggle = useVestCandleOverlayStore((s) => s.toggle);
  const label = enabled ? 'Hide Vest NQ-PERP candles' : 'Show Vest NQ-PERP candles over the CME candles';
  const gapTitle =
    gap === null
      ? 'No Vest candles yet: waiting for Vest prices and at least 3 closed bars on both venues'
      : `Vest prices shifted by ${(-gap).toFixed(2)} pts onto the CME axis (median Vest - CME close over the last ${GAP_WINDOW} closed 2m bars)`;
  return (
    <div className={`vest-candle-toggle ${enabled ? 'is-on' : ''}`} data-testid="vest-candle-toggle-wrap">
      <button
        type="button"
        className="vest-candle-toggle-btn"
        data-testid="vest-candle-toggle"
        aria-label={label}
        aria-pressed={enabled}
        title={label}
        // Keep the tap off the chart below (pan, long-press alert, crosshair).
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
      >
        <CandlestickChart size={13} />
      </button>
      {enabled && (
        <span className="vest-candle-gap" data-testid="vest-candle-gap" title={gapTitle}>
          {gapLabel(gap)}
        </span>
      )}
    </div>
  );
};
